import React, { MutableRefObject, useCallback } from 'react';

import { ListRenderItemInfo, Pressable, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';

import { Filter, FilterOutline } from 'assets/images';
import IconArrowDownSmall from 'assets/images/svgs/ico_arrow_down_small.svg';
import IconArrowUpSmall from 'assets/images/svgs/ico_arrow_up_small.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    EPG_DATE_PICKER_BUTTON_ID,
    EPG_FILTER_BUTTON_ID,
    EPG_FILTER_ITEM_ID,
} from 'config/testID';

import { isPhone, isTabletOrWeb } from 'utils/responsiveness';

import { Position } from 'screens/Epg/hooks/useDatePicker';

import BottomSheetAccessibleList from 'components/BottomSheetAccessibleList';
import BottomSheetList from 'components/BottomSheetList';
import BottomSheetRowItem from 'components/BottomSheetRowItem';
import rowStyles from 'components/BottomSheetRowItem/styles';
import PickerList from 'components/PickerList';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import { EpgFilterCategory } from '../../types';
import styles, {
    PICKER_LIST_HEIGHT,
    PICKER_LIST_WIDTH,
    defaultModalStartingPosition,
} from './styles';

type Props = {
    bottomSheetRef: MutableRefObject<BottomSheetModalMethods | undefined>;
    filterCategories: EpgFilterCategory[];
    filterCategorySelected: boolean;
    isFilterListVisible: boolean;
    screenReaderEnabled: boolean;
    onPressFilter(): void;
    onCloseFilter(): void;
    onPressFilterCategoryItem(filterCategory: EpgFilterCategory): void;
    onDismiss(): void;
    selectedFilterCategory?: string;
    isListLayout?: boolean;
    modalStartingPosition?:
        | Position
        | {
              right: number;
              bottom: number;
          };
};

const {
    ACCESSIBILITY: {
        EPG: { ACCESSIBILITY_EPG_FILTER_ICON },
    },
} = LANGUAGE_KEYS;

function EpgFilter({
    bottomSheetRef,
    filterCategories,
    filterCategorySelected,
    isFilterListVisible,
    screenReaderEnabled,
    onPressFilter,
    onCloseFilter,
    onPressFilterCategoryItem,
    onDismiss,
    isListLayout = false,
    selectedFilterCategory,
    modalStartingPosition = defaultModalStartingPosition,
}: Props) {
    const { t } = useTranslation();

    const handlePress = useCallback(
        (category: EpgFilterCategory) => {
            onPressFilterCategoryItem(category);
            onCloseFilter();
        },
        [onPressFilterCategoryItem],
    );

    const renderBottomSheetRow = useCallback(
        ({ item }: ListRenderItemInfo<EpgFilterCategory>) => (
            <BottomSheetRowItem
                key={item.key}
                testID={`${EPG_FILTER_ITEM_ID}_${item.label}`}
                item={item}
                onPress={handlePress}
                screenReaderEnabled={screenReaderEnabled}
            />
        ),
        [onPressFilterCategoryItem, screenReaderEnabled],
    );

    return (
        <>
            {isListLayout ? (
                <Pressable
                    testID={EPG_DATE_PICKER_BUTTON_ID}
                    style={styles.pickerButtonContainer}
                    onPress={onPressFilter}
                    dataSet={{ class: 'epgDatePicker' }}
                    accessible
                    accessibilityRole="button"
                    accessibilityLabel={t(ACCESSIBILITY_EPG_FILTER_ICON)}
                >
                    <Text
                        accessible={false}
                        category={ETextCategory.caption1}
                        color={ETextColor.neutral}
                        bold
                    >
                        {selectedFilterCategory}
                    </Text>
                    <View style={styles.svgArrowContainer} accessible={false}>
                        {isFilterListVisible && isTabletOrWeb ? (
                            <IconArrowUpSmall />
                        ) : (
                            <IconArrowDownSmall />
                        )}
                    </View>
                </Pressable>
            ) : (
                <Pressable
                    testID={EPG_FILTER_BUTTON_ID}
                    accessibilityLabel={t(ACCESSIBILITY_EPG_FILTER_ICON)}
                    accessibilityRole="button"
                    style={styles.root}
                    onPress={onPressFilter}
                >
                    {filterCategorySelected ? <Filter /> : <FilterOutline />}
                </Pressable>
            )}
            {isTabletOrWeb && (
                <PickerList
                    data={filterCategories}
                    isModalVisible={isFilterListVisible}
                    modalStartingPosition={modalStartingPosition}
                    listHeight={PICKER_LIST_HEIGHT}
                    listWidth={PICKER_LIST_WIDTH}
                    rowHeight={rowStyles.container.height}
                    renderItem={renderBottomSheetRow}
                    keyExtractor={keyExtractor}
                    onClose={onCloseFilter}
                />
            )}
            {isPhone && !screenReaderEnabled && (
                <BottomSheetList
                    ref={bottomSheetRef}
                    data={filterCategories}
                    renderRowItem={renderBottomSheetRow}
                    keyExtractor={keyExtractor}
                    rowHeight={rowStyles.container.height}
                    headerTitle={t(LANGUAGE_KEYS.EPG.EPG_FILTER_TITLE)}
                    onClose={onCloseFilter}
                    onDismiss={onDismiss}
                />
            )}
            {isPhone && screenReaderEnabled && (
                <BottomSheetAccessibleList
                    visible={isFilterListVisible}
                    data={filterCategories}
                    renderRowItem={renderBottomSheetRow}
                    keyExtractor={keyExtractor}
                    headerTitle={t(LANGUAGE_KEYS.EPG.EPG_FILTER_TITLE)}
                    onClose={onCloseFilter}
                />
            )}
        </>
    );
}

export default EpgFilter;

function keyExtractor({ key }: EpgFilterCategory) {
    return key;
}
